/**
 * Lawyer-tester pipeline transparency: the shared step-event contract.
 *
 * Single source of truth for the `pipeline_step_events` wire shape, replacing
 * two independently-duplicated definitions that existed before pirx-4wg3x.1:
 * backend's `ClaimStepName` (methodology/orchestrator/telemetry.ts) and
 * frontend's own `PipelineStepEvent` interface
 * (services/api/pipeline-transparency-client.ts). Backend's DB-row type
 * (`db/schema/audits.ts`'s Drizzle-inferred `PipelineStepEvent`, with `Date`
 * columns) and its pre-serialization service view
 * (`services/internal/pipeline-transparency.service.ts`'s
 * `PipelineStepEventView`) are intentionally NOT collapsed into this schema —
 * this is the over-the-wire (JSON, string dates) shape both backend response
 * bodies and the frontend client actually exchange.
 *
 * `stage`/`step` stay `z.string()` here (not a closed enum) because the
 * epic that founded this module (pirx-4wg3x) added step-emitting pipelines
 * beyond methodology's own spine; `ClaimStepName` below is the shared
 * step vocabulary, now single-sourced here for type safety.
 */

import { z } from "zod";
import type { AnalysisModeAxis } from "./types.ts";

/**
 * Shared step vocabulary for methodology, audit-claim, assessment, retrieval,
 * evidence, and rewrite pipelines; individual pipelines emit subsets of these
 * steps. Relocated from
 * backend/src/services/methodology/orchestrator/telemetry.ts (pirx-4wg3x.1),
 * extended by the per-claim audit pipeline steps below.
 */
export const claimStepNameSchema = z.enum([
  "rag_retrieval",
  "report_generation",
  "post_grounding_enrichment",
  "quote_grounding_check",
  "finding_recommendation_coherence_check",
  "citation_grounding_validation",
  "rewrite_synthesis",
  "rewrite_loop",
  "executive_summary",
  "methodology_trace",
  "post_grounding_review",
  // Nested inside "post_grounding_enrichment" (pirx-b5376): each of
  // post_grounding_enrichment's own LLM call sites gets its own step event
  // with a real llm_interaction_id FK, alongside (not replacing) the
  // existing outer "post_grounding_enrichment" event.
  "gap_detection",
  "classification_verifier",
  "challenge_pass",
  "factual_coherence_check",
  // Live per-claim audit pipeline (pirx-4wg3x.2,
  // audit-claim-processing.ts): risk and recommendation steps below.
  // Non-GREEN claims continue through report assembly, grounding, rewrite,
  // summary, trace, and review stages (audits' own stage runners in the
  // same file).
  "risk_assessment",
  "recommendation_generation",
  // Live per-claim report assembly step (pirx-t8bz1, audit-claim-processing.ts) —
  // deterministic local field shaping, decoupled from the Gemini report_generation call.
  "claim_report_assembly",
  // Assessment/report pipeline (pirx-4wg3x.3, assessment-pipeline.ts /
  // report-generation.ts) — "report_generation" above is reused as-is (same
  // underlying gemini/stages/report-generator.ts call as methodology's own).
  "commercial_practice_classification",
  "summary_refinement",
  // Evidence-matching + evidence gap-detection pipelines (pirx-4wg3x.5,
  // services/audits/evidence/*.ts) — distinct from methodology's own "gap_detection"
  // (gemini/stages/gap-detector.ts, a different module/data-shape/caller).
  "evidence_matching",
  "evidence_gap_detection",
  // Messaging-generation pipeline (pirx-4wg3x.6, messaging-generator.ts) —
  // can fire up to 3x per claim (once per tone variant: conservative/
  // balanced/confident).
  "messaging_generation",
  // Raw claim-extraction (pirx-4wg3x follow-up pirx-03c0h,
  // vertex-ai/claim-extractor.ts) — the pre-analysis step run once per audit
  // before any claim identity exists (claim_id is null for this step); can
  // fire more than once only on the rare truncation/chunking fallback path.
  "claim_extraction",
  // Canonical product-domain classification over the extracted source. Runs
  // once per audit after the audit-owned provenance draft exists.
  "source_product_domain_classification",
  // Corrected-document rewrite (pirx-4wg3x follow-up pirx-ejoql,
  // pipeline-steps/generate-fixed-document.ts, the "Ulepszona wersja"
  // feature) — runs once per audit, only when there are RED/YELLOW claims.
  "corrected_document_generation",
  // Evidence parsing (pirx-4wg3x follow-up pirx-wwf74): OCR transcription of
  // image evidence (evidence/image-to-text.ts) and the downstream document
  // parse (vertex-ai/evidence.ts's parseEvidenceDocument) — both upstream of
  // the already-instrumented evidence_matching/evidence_gap_detection steps,
  // fire once per uploaded evidence file/image, before claim identity exists.
  "evidence_image_ocr",
  "evidence_document_parsing",
  // Nested inside "report_generation" (pirx-4wg3x follow-up pirx-2o5ef,
  // gemini/stages/coverage-handler.ts's generateSection7DeepScan) — mirrors
  // the pirx-b5376 pattern of nested per-call events alongside (not
  // replacing) the outer report_generation event.
  "deep_scan_section7",
  // Nested inside "rag_retrieval" (pirx-dnn42.1): the query-generation LLM
  // call that proposes up to 3 alternate search queries for a claim, wired
  // into its own step event alongside (not replacing) the outer
  // "rag_retrieval" event — same nesting pattern as post_grounding_enrichment
  // above.
  "rag_query_generation",
  // Custom rewrite styles v1 (pirx-4i1vw, epic pirx-2gm7r): Stage-2
  // per-claim / per-document restyle passes over an already-generated
  // compliant output — a separate concern from the per_claim audit
  // pipeline above. claim_restyle fires once per RED/YELLOW claim with a
  // non-empty saferPhrasing; document_restyle fires at most once per job
  // (claim_id is null for this step, same convention as claim_extraction).
  "claim_restyle",
  "document_restyle",
]);
export type ClaimStepName = z.infer<typeof claimStepNameSchema>;

/**
 * i18n key suffix (under `internal:stepDescriptions.*`) for each step name's
 * contextualizing description, shown in Claim view and Timing view
 * (pirx-9azdw). Keyed 1:1 against `claimStepNameSchema` and guarded by a
 * completeness test below — a step added to that enum without a matching
 * entry here fails the test immediately, the same discipline
 * `pipeline-instrumentation-completeness.test.ts` applies to instrumentation
 * call sites.
 */
export const STEP_DESCRIPTION_KEYS: Record<ClaimStepName, string> = {
  rag_retrieval: "ragRetrieval",
  report_generation: "reportGeneration",
  post_grounding_enrichment: "postGroundingEnrichment",
  quote_grounding_check: "quoteGroundingCheck",
  finding_recommendation_coherence_check: "findingRecommendationCoherenceCheck",
  citation_grounding_validation: "citationGroundingValidation",
  rewrite_synthesis: "rewriteSynthesis",
  rewrite_loop: "rewriteLoop",
  executive_summary: "executiveSummary",
  methodology_trace: "methodologyTrace",
  post_grounding_review: "postGroundingReview",
  gap_detection: "gapDetection",
  classification_verifier: "classificationVerifier",
  challenge_pass: "challengePass",
  factual_coherence_check: "factualCoherenceCheck",
  risk_assessment: "riskAssessment",
  recommendation_generation: "recommendationGeneration",
  claim_report_assembly: "claimReportAssembly",
  commercial_practice_classification: "commercialPracticeClassification",
  summary_refinement: "summaryRefinement",
  evidence_matching: "evidenceMatching",
  evidence_gap_detection: "evidenceGapDetection",
  messaging_generation: "messagingGeneration",
  claim_extraction: "claimExtraction",
  source_product_domain_classification: "sourceProductDomainClassification",
  corrected_document_generation: "correctedDocumentGeneration",
  evidence_image_ocr: "evidenceImageOcr",
  evidence_document_parsing: "evidenceDocumentParsing",
  deep_scan_section7: "deepScanSection7",
  rag_query_generation: "ragQueryGeneration",
  claim_restyle: "claimRestyle",
  document_restyle: "documentRestyle",
};

/**
 * Returns the i18n key suffix for a step's description, or null for an
 * unregistered step name. Takes a plain `string`, not `ClaimStepName` —
 * live `pipeline_step_events.step` values aren't validated against this
 * enum on the wire (see this file's header comment), so this must degrade
 * gracefully rather than throw.
 */
export function getStepDescriptionKey(step: string): string | null {
  return (STEP_DESCRIPTION_KEYS as Record<string, string>)[step] ?? null;
}

/**
 * Per-step manifest entry (pirx-mw652): where a step is implemented, which
 * prompt-catalog builder (if any) it invokes, and whether it fires on a real
 * customer audit or only from eval/benchmark entry points
 * (`POST /api/methodology/pipeline/run`, `whole_text` benchmark harness,
 * `scripts/verifier-eval.ts`). Established once by reading every
 * `recordClaimStepEvent(ctx, "<step>", ...)` call site and tracing each back
 * to its route/queue-worker wiring — see pirx-4wg3x's own closeout for why
 * this determination is easy to get wrong twice (both a multi-agent review
 * and a full epic mis-assumed the live per-claim pipeline before landing on
 * the correct answer).
 */
export interface StepManifestEntry {
  /** `file:line` of the primary `recordClaimStepEvent` call site(s) for this step. */
  sourceLocation: string;
  /** Key into `backend/src/prompts/ids.ts`'s `PROMPT_IDS`, or null when the
   * step is deterministic (no LLM call) or its prompt isn't yet centralized
   * in `backend/src/prompts/catalog.ts`. */
  promptCatalogId: string | null;
  /** True iff at least one emitting call site is reachable from a real
   * customer audit/assessment (an HTTP route or queue worker started by
   * `queue-manager.ts`), not only from an eval/benchmark entry point. */
  isLive: boolean;
}

export const CLAIM_STEP_MANIFEST: Record<ClaimStepName, StepManifestEntry> = {
  rag_retrieval: {
    sourceLocation:
      "backend/src/services/audits/rag/audit-rag-queue-jobs.ts:488 (live); backend/src/services/audits/claims/audit-claim-processing.ts:397 (live); backend/src/services/methodology/shared/stages/rag-retrieval.ts:47 (eval-only)",
    promptCatalogId: null,
    isLive: true,
  },
  report_generation: {
    sourceLocation:
      "backend/src/services/assessments/report-generation.ts:262 (live); backend/src/services/methodology/shared/stages/report-generation.ts:52 (eval-only)",
    promptCatalogId: "GEMINI_REPORT_STRUCTURED_INPUT",
    isLive: true,
  },
  post_grounding_enrichment: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/post-grounding.ts:73",
    promptCatalogId: null,
    isLive: false,
  },
  quote_grounding_check: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/verification.ts:136",
    promptCatalogId: null,
    isLive: false,
  },
  finding_recommendation_coherence_check: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/verification.ts:169",
    promptCatalogId: null,
    isLive: false,
  },
  citation_grounding_validation: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/verification.ts:197",
    promptCatalogId: null,
    isLive: false,
  },
  rewrite_synthesis: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/rewrite.ts:133",
    promptCatalogId: "GEMINI_REWRITE_SYNTHESIS_USER",
    isLive: false,
  },
  rewrite_loop: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/rewrite.ts:166",
    promptCatalogId: null,
    isLive: false,
  },
  executive_summary: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/executive-summary.ts:68",
    promptCatalogId: null,
    isLive: false,
  },
  methodology_trace: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/methodology-trace.ts:97",
    promptCatalogId: null,
    isLive: false,
  },
  post_grounding_review: {
    sourceLocation:
      "backend/src/services/methodology/shared/stages/post-grounding.ts:125",
    promptCatalogId: null,
    isLive: false,
  },
  gap_detection: {
    sourceLocation: "backend/src/services/methodology/report/generation.ts:177",
    promptCatalogId: "GEMINI_GAP_DETECTION_USER",
    isLive: false,
  },
  classification_verifier: {
    sourceLocation:
      "backend/src/services/methodology/grounding/post-enrichment.ts:200 (nested inside post_grounding_enrichment)",
    promptCatalogId: "GEMINI_CLASSIFICATION_VERIFIER_USER",
    isLive: false,
  },
  challenge_pass: {
    sourceLocation:
      "backend/src/services/methodology/grounding/post-enrichment.ts:257 (nested inside post_grounding_enrichment)",
    promptCatalogId: "GEMINI_CHALLENGE_PASS_USER",
    isLive: false,
  },
  factual_coherence_check: {
    sourceLocation:
      "backend/src/services/methodology/grounding/post-enrichment.ts:556 (nested inside post_grounding_enrichment)",
    promptCatalogId: null,
    isLive: false,
  },
  // pirx-wj03p: KROK 0-4 decision tree lives in
  // backend/src/services/methodology/risk-decision-tree/; the only LLM
  // sub-calls (environmental-claim-gate.ts, krok4-misleading-test.ts) share
  // LlmOperation key "risk_assessment_verifier" -- not a single prompt id --
  // so promptCatalogId is null and the operation key is named in sourceLocation.
  risk_assessment: {
    sourceLocation:
      'backend/src/services/audits/claims/audit-claim-processing.ts:178 (wraps assessClaimRisk -- deterministic KROK 0-4 tree; 2 LLM sub-calls share operation key "risk_assessment_verifier")',
    promptCatalogId: null,
    isLive: true,
  },
  recommendation_generation: {
    sourceLocation:
      "backend/src/services/audits/claims/audit-claim-processing.ts:328",
    promptCatalogId: "VERTEX_CLAIMS_RECOMMENDATION",
    isLive: true,
  },
  claim_report_assembly: {
    sourceLocation:
      "backend/src/services/audits/claims/audit-claim-processing.ts:465",
    promptCatalogId: null,
    isLive: true,
  },
  commercial_practice_classification: {
    sourceLocation:
      "backend/src/services/assessments/report-generation.ts:192 (live); backend/src/services/audits/audit-queue.ts:379 (live)",
    promptCatalogId: null,
    isLive: true,
  },
  summary_refinement: {
    sourceLocation:
      "backend/src/services/assessments/assessment-pipeline.ts:303",
    promptCatalogId: "VERTEX_SUMMARY_REFINEMENT",
    isLive: true,
  },
  evidence_matching: {
    sourceLocation:
      "backend/src/services/audits/evidence/evidence-matcher.ts:156 + kb-evidence-matcher.ts:182 (both reachable live via services/audits/analysis.ts)",
    promptCatalogId: "VERTEX_EVIDENCE_MATCH_CLAIM",
    isLive: true,
  },
  evidence_gap_detection: {
    sourceLocation:
      "backend/src/services/audits/evidence/gap-detector.ts:275 (reachable live via services/audits/analysis.ts)",
    promptCatalogId: "VERTEX_GAPS_COMBINED",
    isLive: true,
  },
  messaging_generation: {
    sourceLocation:
      "backend/src/services/audits/evidence/messaging-generator.ts:317 (reachable live via services/audits/analysis.ts's generateMessaging)",
    promptCatalogId: "MESSAGING_GENERATION",
    isLive: true,
  },
  claim_extraction: {
    sourceLocation: "backend/src/services/audits/audit-runner.ts:486",
    promptCatalogId: "VERTEX_CLAIMS_EXTRACTION",
    isLive: true,
  },
  source_product_domain_classification: {
    sourceLocation: "backend/src/services/audits/audit-queue.ts",
    promptCatalogId: "SOURCE_PRODUCT_DOMAIN_CLASSIFIER_SYSTEM",
    isLive: true,
  },
  corrected_document_generation: {
    sourceLocation:
      "backend/src/services/audits/rag/audit-rag-finalization.ts:218",
    promptCatalogId: null,
    isLive: true,
  },
  evidence_image_ocr: {
    sourceLocation:
      "backend/src/services/audits/evidence/image-to-text.ts:233 (reachable live via files/text-extractor.ts, called from the live audit-queue upload flow)",
    promptCatalogId: "VERTEX_EVIDENCE_IMAGE_TO_TEXT",
    isLive: true,
  },
  evidence_document_parsing: {
    sourceLocation:
      "backend/src/services/vertex-ai/evidence.ts:94 (reachable live via services/audits/evidence/evidence-parser.ts, called from services/audits/analysis.ts)",
    promptCatalogId: "VERTEX_EVIDENCE_PARSE_DOCUMENT",
    isLive: true,
  },
  deep_scan_section7: {
    sourceLocation:
      "backend/src/services/gemini/stages/report-generator.ts:284,473 (nested inside report_generation, both call sites; live via the assessment-pipeline call site)",
    promptCatalogId: "GEMINI_SECTION7_DEEP_SCAN_USER",
    isLive: true,
  },
  rag_query_generation: {
    sourceLocation:
      "backend/src/services/rag/query-generator.ts:449 (nested inside rag_retrieval by design, but NOT yet wired into any rag_retrieval call site — the only callers found are its own test file)",
    promptCatalogId: "RAG_QUERY_GENERATION",
    isLive: false,
  },
  claim_restyle: {
    sourceLocation: "backend/src/services/styles/restyle-worker.ts:660",
    promptCatalogId: "STYLE_CLAIM_RESTYLE_USER",
    isLive: true,
  },
  document_restyle: {
    sourceLocation: "backend/src/services/styles/restyle-worker.ts:726",
    promptCatalogId: "STYLE_DOCUMENT_RESTYLE_USER",
    isLive: true,
  },
};

/**
 * Returns the manifest entry for a step, or null for an unregistered step
 * name. Takes a plain `string`, not `ClaimStepName`, for the same reason
 * `getStepDescriptionKey` does.
 */
export function getStepManifestEntry(step: string): StepManifestEntry | null {
  return (
    CLAIM_STEP_MANIFEST as Record<string, StepManifestEntry>
  )[step] ?? null;
}

/** Mirrors the `pipeline_step_event_status` Postgres enum (db/schema/audits.ts). */
export const pipelineStepEventStatusSchema = z.enum([
  "running",
  "completed",
  "failed",
  "skipped",
]);
export type PipelineStepEventStatus = z.infer<
  typeof pipelineStepEventStatusSchema
>;

/**
 * Mirrors the `pipeline_step_event_skip_reason` Postgres enum
 * (db/schema/audits.ts) — the reason code for a `status: "skipped"` step
 * event (pirx-claoz). Only meaningful when `status === "skipped"`.
 */
export const pipelineStepEventSkipReasonSchema = z.enum([
  "insufficient_grounding",
  "rag_deadline_exceeded",
  "rag_retrieval_failed_will_retry",
]);
export type PipelineStepEventSkipReason = z.infer<
  typeof pipelineStepEventSkipReasonSchema
>;

const ragChunkForEventSchema = z.object({
  sourceDocument: z.string(),
  articleReference: z.string().optional(),
  frameworkName: z.string().optional(),
  relevanceScore: z.number().optional(),
  snippet: z.string().optional(),
});

/**
 * Bounded blob written by `boundRagChunks` (backend
 * grounding/rag-retrieval-handler.ts) — only present on `rag_retrieval`
 * steps. `truncated` lives inside the blob (not a separate column) so the
 * AC8 truncation indicator always travels with the data it describes.
 */
export const pipelineRagChunksBlobSchema = z.object({
  chunks: z.array(ragChunkForEventSchema),
  truncated: z.boolean(),
  totalRetrieved: z.number(),
});
export type PipelineRagChunksBlob = z.infer<
  typeof pipelineRagChunksBlobSchema
>;

/**
 * Full RAG-stage transparency trace (pirx-dnn42, Item 5) — attached to a
 * `rag_retrieval` step event via the `ragTrace` field below. Small enough
 * (params + queries + per-stage counts, no raw candidate payloads) to keep
 * riding the polled `getPipelineRunEvents` response; the ~80-120 raw
 * pre-rank candidates are a SEPARATE, much larger blob (see
 * `ragRawCandidateRowSchema` below) that must never be nested in here — see
 * this epic's write-boundary guard in the spec.
 */
const ragTraceParamSchema = z.object({
  name: z.string(),
  value: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
  ]),
  source: z.enum(["env-default", "request-override"]),
});
export type RagTraceParam = z.infer<typeof ragTraceParamSchema>;

const ragTraceQuerySchema = z.object({
  query: z.string(),
  kind: z.enum(["base", "expansion"]),
  ensembleRun: z.number().optional(),
  focus: z.string().optional(),
  rationale: z.string().optional(),
  resultCount: z.number(),
  // True when the underlying search errored — distinct from a real query
  // that legitimately found 0 results (see `executeExpansionFallback`'s
  // batch-failure case in query-expansion.ts).
  failed: z.boolean().optional(),
  // Resolved client-side against the already-fetched `interactions` list —
  // never a backend join (see the spec's "no new backend join" note).
  queryGenerationInteractionId: z.string().optional(),
});
export type RagTraceQuery = z.infer<typeof ragTraceQuerySchema>;

const ragTraceStageSchema = z.object({
  // Deliberately z.string(), not a closed z.enum() — this schema reads
  // ALREADY-PERSISTED historical rows, not just what current code writes.
  // An enum here re-breaks atomically on every future stage rename:
  // pirx-xpd86 (2026-08-30) hit exactly this when 120c83b76 renamed
  // "diversity" to "rerank"/"relevance_floor" without keeping the old
  // literal, and
  // getPipelineRunEvents's `.parse()` fails the ENTIRE events array (not
  // just the one stage) on one unrecognized value — 500ing the
  // pipeline-transparency events endpoint for every run whose stored
  // ragTrace still had the old name (633 runs / 13804 events on the dev
  // DB). A string never re-breaks on rename; nothing here switches
  // exhaustively on `name` (frontend renders it as raw text — see
  // `rag-blocks.tsx`'s `RagTraceSection`), so there is no correctness
  // dependency on a closed union at read time.
  //
  // Current stage names emitted by the RAG pipeline (query-trace.ts,
  // rag-retrieval-handler.ts, audit-rag-queue-jobs.ts,
  // shared/stages/rag-retrieval.ts), for reference only — not enforced
  // here: "base_search", "expansion_search", "merge", "dedup", "rerank",
  // "relevance_floor", "final_cap", "grounding_gate", "persisted_cap".
  name: z.string(),
  inputCount: z.number(),
  outputCount: z.number(),
  durationMs: z.number().optional(),
});
export type RagTraceStage = z.infer<typeof ragTraceStageSchema>;

export const ragRetrievalTraceSchema = z.object({
  params: z.array(ragTraceParamSchema),
  resultCache: z.enum(["hit", "miss"]),
  // True on a resultCache hit — `queries`/`stages` are empty, never replayed
  // from a prior call's data.
  servedFromCache: z.boolean(),
  // Absent when query expansion never ran (no LLM call to have cached).
  queryGenerationCache: z.enum(["hit", "miss"]).optional(),
  queries: z.array(ragTraceQuerySchema),
  // Open list — the live per-claim pipeline legitimately never appends
  // "grounding_gate" (no grounding gate runs on that path); a run having one
  // fewer stage than another is a real pipeline difference, not an error.
  stages: z.array(ragTraceStageSchema),
});
export type RagRetrievalTrace = z.infer<typeof ragRetrievalTraceSchema>;

/**
 * One raw pre-rank Qdrant candidate (pirx-dnn42, Item 2.3) — the ~80-120
 * hits captured immediately after the base+expansion merge, before
 * dedup/diversity/rerank narrow the set down to the ranked top-25 in
 * `ragChunkForEventSchema` above. Deliberately reuses that shape MINUS
 * `snippet` (the byte-size driver — a surviving candidate's snippet is
 * already available on the ranked `rag_chunks` blob), plus a `phase` tag so
 * the UI can group by source query.
 *
 * NEVER add this to `pipelineStepEventSchema` — the raw-candidate set must
 * never appear in the polled `getPipelineRunEvents` response, at any
 * nesting depth. It is served only via the dedicated on-demand
 * `GET /api/internal/pipeline-step-events/:stepEventId/rag-raw-candidates`
 * route, fetched lazily when a tester expands that step's panel.
 */
export const ragRawCandidateRowSchema = z.object({
  sourceDocument: z.string(),
  articleReference: z.string().optional(),
  frameworkName: z.string().optional(),
  relevanceScore: z.number().optional(),
  // "base" for the primary claim-text query, "expansion:<n>" (1-indexed) for
  // an expansion query. A plain string (not a strict literal union) so a
  // future change to the expansion-query count doesn't require a schema
  // change here.
  phase: z.string(),
});
export type RagRawCandidateRow = z.infer<typeof ragRawCandidateRowSchema>;

/**
 * Result of `GET /api/internal/llm-interactions/:interactionId/prompt-archive`
 * (CT8, `llm-logger.ts`): the archived full prompt body for ONE interaction,
 * fetched on demand. `requestPrompt` on every polled/combined-view payload
 * is deliberately never the raw rendered prompt (a truncated cataloged-
 * prompt preview, or the fixed "[uncataloged prompt...]" marker) -- this is
 * the only path to the real text. `available: false` covers both "never
 * archived" (best-effort upload failed at write time) and "archived object
 * no longer exists" (30-day bucket TTL) -- neither is an error state for
 * the caller to branch on differently.
 */
export const llmInteractionPromptArchiveResultSchema = z.discriminatedUnion(
  "available",
  [
    z.object({ available: z.literal(true), promptText: z.string() }),
    z.object({ available: z.literal(false) }),
  ],
);
export type LlmInteractionPromptArchiveResult = z.infer<
  typeof llmInteractionPromptArchiveResultSchema
>;

/**
 * Character-offset span identifying one injected (per-run) value within a
 * rendered prompt — mirrors backend's `DynamicSpan`
 * (`backend/src/prompts/dynamic-spans.ts`). Powers the pipeline-
 * transparency inline-highlighting feature (spec:
 * docs/plans/2026-08-03-prompt-injected-input-inline-highlighting-spec.md);
 * a superset, in principle, of `promptDynamicFields` below — populated only
 * for call sites that have been migrated to `dynamic-spans.ts`'s
 * `buildPromptText`/`inject` or `renderPromptTemplate`, independently of
 * `promptDynamicFields`'s own coverage. Never derive one from the other.
 */
export const dynamicSpanSchema = z.object({
  field: z.string(),
  start: z.number(),
  end: z.number(),
});
export type DynamicSpanView = z.infer<typeof dynamicSpanSchema>;

export const pipelineStepEventLlmInteractionViewSchema = z.object({
  id: z.string(),
  requestPrompt: z.string(),
  responseRaw: z.string().nullable(),
  modelName: z.string(),
  latencyMs: z.number().nullable(),
  /**
   * Only set for `generate_fixed_document` rows (from that call's logged
   * `metadata.originalDocumentText`) — the clean source document text,
   * distinct from `requestPrompt` (the full constructed prompt). Powers the
   * pipeline-transparency before/after diff view; absent for every other
   * operation.
   */
  originalDocumentText: z.string().optional(),
  /**
   * Field names substituted into the prompt by the centralized
   * `buildPrompt`/`renderPromptTemplate` path (`PromptBuildResult.dynamicInputs`,
   * `backend/src/prompts/types.ts`) — e.g. `["claim", "corpusDescription"]`.
   * Populated only for call sites that thread it through their
   * `logLlmInteraction` metadata; absent (not empty) for prompts assembled
   * outside that system.
   */
  promptDynamicFields: z.array(z.string()).optional(),
  /** Character-offset spans within `requestPrompt` — see dynamicSpanSchema's doc comment. */
  promptDynamicSpans: z.array(dynamicSpanSchema).optional(),
  /**
   * Cataloged prompt identity (pirx-1ryur T4/T5, spec
   * docs/specs/prompt-version-registry.md) that produced `requestPrompt`,
   * and the `PROMPT_VERSIONS` version it resolved to at write time. Both
   * null (never coerced to a placeholder) for rows written before T4
   * landed, or when the call site never threaded a cataloged `PromptId`.
   */
  promptId: z.string().nullable(),
  promptVersion: z.string().nullable(),
});
export type PipelineStepEventLlmInteractionView = z.infer<
  typeof pipelineStepEventLlmInteractionViewSchema
>;

/**
 * Mirrors the `audit_status` Postgres enum (`db/schema/audits.ts`) — the
 * parent audit's own status, exposed on the Phase 1 events response
 * (pirx-t3zyh.1) so the frontend can distinguish a genuinely unsupported
 * pre-Phase-1 audit (terminal status, zero events) from one that simply
 * hasn't produced any steps yet (non-terminal status, zero events).
 */
export const pipelineRunStatusSchema = z.enum([
  "UPLOADING",
  "QUEUED",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
]);
export type PipelineRunStatus = z.infer<typeof pipelineRunStatusSchema>;

/**
 * Mirrors `AnalysisModeAxis` (`packages/shared/src/types.ts`) — the D8
 * report commentary / analysis-mode axis (CSR mode). Duplicated as its own
 * zod enum here, same convention as `pipelineRunStatusSchema` mirroring the
 * `audit_status` Postgres enum, so this file's schemas stay self-contained.
 * `ANALYSIS_MODE_AXIS_PARITY_CHECK` below (same shape as
 * `db/schema/audits.ts`'s `AUDIT_STATUS_PARITY_CHECK`) still catches drift
 * between the two independent definitions at compile time, without
 * collapsing them into one import and giving up self-containment.
 */
export const analysisModeAxisSchema = z.enum(["commercial", "csr"]);
export type AnalysisModeAxisWire = z.infer<typeof analysisModeAxisSchema>;

export const ANALYSIS_MODE_AXIS_PARITY_CHECK: AnalysisModeAxisWire extends
  AnalysisModeAxis
  ? (AnalysisModeAxis extends AnalysisModeAxisWire ? true : never)
  : never = true;

/** Mirrors `ClaimSubtype` (backend `services/greenwashing/subtype.ts`) — CSR-mode reputational subtype classification, present only when a claim's `basis` is `"reputational"`. */
export const claimSubtypeSchema = z.enum([
  "no-impact",
  "comparative",
  "trend",
  "none",
]);
export type ClaimSubtypeWire = z.infer<typeof claimSubtypeSchema>;

/** Mirrors `ClaimCardBasis` (backend `services/greenwashing/csr-claim-card-fields.ts`) — whether this claim's report framing is legal (citations, UCPD classes) or CSR-mode reputational (legal citations dropped). */
export const claimCardBasisSchema = z.enum(["legal", "reputational"]);
export type ClaimCardBasisWire = z.infer<typeof claimCardBasisSchema>;

/** Mirrors `ClaimCheckStatus` (backend `services/greenwashing/section-5-8-evaluator.ts`). */
export const claimCheckStatusSchema = z.enum([
  "step0-out",
  "not-a-claim",
  "step4-no-grounds",
  "scored",
]);
export type ClaimCheckStatusWire = z.infer<typeof claimCheckStatusSchema>;

/** Mirrors `Section58ScopeWarning` (backend `services/greenwashing/section-5-8-evaluator.ts`) — set only when `checkStatus` is `"step4-no-grounds"` because the audit's declared `industrySector` was absent/unrecognized, not because a recognized sector genuinely had no precedent match (pirx-ou313). */
export const claimScopeWarningSchema = z.enum(["missing-industry-sector"]);
export type ClaimScopeWarningWire = z.infer<typeof claimScopeWarningSchema>;

/**
 * CSR-mode outcome for one claim (pirx-cijf6.4, surfaced on the
 * pipeline-transparency page) — the exact fields
 * `buildCsrClaimCardFields`/`resolveClaimAnalysisMode` compute for the
 * production claim card, recomputed read-only here from the audit's
 * declared context plus this claim's stored `riskLevel`. Attached to every
 * `pipeline_step_events` row sharing this `claimId` (same per-row
 * duplication convention `claimText` already uses above), not deduplicated
 * to one row per claim.
 */
export const pipelineTransparencyClaimCsrModeSchema = z.object({
  declaredMode: analysisModeAxisSchema,
  /**
   * Differs from `declaredMode` when a declared `"csr"` claim carries
   * RED/YELLOW risk and flips to commercial framing
   * (`shouldFlipCsrModeToCommercial`) — the fact that explains why a
   * claim's `basis` below can be `"legal"` even on a declared-CSR audit.
   */
  effectiveMode: analysisModeAxisSchema,
  subtype: claimSubtypeSchema.optional(),
  basis: claimCardBasisSchema.optional(),
  checkStatus: claimCheckStatusSchema.optional(),
  scopeWarning: claimScopeWarningSchema.optional(),
});
export type PipelineTransparencyClaimCsrMode = z.infer<
  typeof pipelineTransparencyClaimCsrModeSchema
>;

/**
 * Judge verdict for one segment of a run (pirx-xmvo3.2, spec
 * docs/specs/backend-judge-scoring-for-ci95-evaluator.md) -- attached to a
 * `pipeline_step_events` row whose `stage` is `"judge_scoring"` and whose
 * `step` is the judged segment, via the `judge_score` JSONB column. Written
 * by an async, post-hoc pass that runs only AFTER the segment's real output
 * is already generated, stored, and returned, so a judge outage can never
 * add latency or a failure mode to the user-facing request.
 *
 * `judgeScores` is the field the regression harness's `ci95` evaluator
 * consumes verbatim (`cli/src/lib/regression/evaluators/ci95.ts`): one score
 * in [0, 1] per rubric criterion, positionally aligned with `criteria`. The
 * criterion count is fixed per rubric version (NOT per claim count) so the
 * CI95 band a run produces is comparable to the band its baseline produced
 * -- a varying sample size would make band overlap meaningless as a
 * regression signal.
 *
 * A failed or timed-out judge call leaves this blob ABSENT (null) and records
 * `status: "failed"` on the judge row instead. There is never a partial blob:
 * every consumer may assume that a present blob has a complete, in-range
 * `judgeScores` of exactly `criteria.length` entries.
 */
export const pipelineJudgeScoreBlobSchema = z.object({
  /** One score in [0, 1] per rubric criterion, ordered as `criteria`. */
  judgeScores: z.array(z.number().min(0).max(1)).min(1),
  /** Stable rubric identity, e.g. `"report_generation_quality"`. */
  rubricId: z.string().min(1),
  /** Rubric version the score was produced under, e.g. `"v1"`. Bump on any
   *  criterion or prompt change -- scores across versions are not
   *  comparable, and a baseline pinned under an older version should be
   *  re-promoted rather than silently compared. */
  rubricVersion: z.string().min(1),
  /** Criterion ids, positionally aligned with `judgeScores`. */
  criteria: z.array(z.string().min(1)).min(1),
  /** Raw judge critique text -- why the score is what it is. Kept for
   *  debugging a surprising score, mirroring how `rawCritique` already flows
   *  through the CLI's `extractRunnerFields`. Bounded: llm-config.json's
   *  judge_scoring operation caps maxOutputTokens at 2048, but the schema
   *  enforces the bound rather than relying on an upstream config value. */
  critique: z.string().max(10_000),
  /** Resolved model name that produced the verdict. */
  model: z.string(),
  /** ISO timestamp the verdict was persisted. */
  scoredAt: z.string(),
  /** Set true when this verdict was replayed from `judge_verdict_cache`
   *  rather than produced by a fresh LLM call. Distinguishes a real
   *  judgment from a cache hit so downstream consumers (audit log, user
   *  notification) can label the source correctly. */
  cached: z.boolean().optional(),
});
export type PipelineJudgeScoreBlob = z.infer<
  typeof pipelineJudgeScoreBlobSchema
>;

/** One `pipeline_step_events` row, as returned by `GET /api/internal/pipeline-runs/:runId/events`. */

export const pipelineStepEventSchema = z.object({
  id: z.string(),
  claimId: z.string().nullable(),
  /**
   * The linked claim's `claim_text` (pirx-t3zyh.1), joined from `claims` via
   * `claimId`. Null whenever `claimId` is null, and also when `claimId`
   * points at a claim row that no longer exists (LEFT JOIN miss) — never a
   * dropped event.
   */
  claimText: z.string().nullable(),
  stage: z.string(),
  step: z.string(),
  status: z.string(),
  /**
   * pirx-claoz: when `status === "skipped"`, the reason code from
   * `pipelineStepEventSkipReasonSchema` (mirrors the
   * `pipeline_step_event_skip_reason` Postgres enum). Null for every
   * non-skipped status; omitted from the wire entirely for pre-epic rows
   * that never carried the column, so existing fixtures keep
   * type-checking the same way `ragTrace`/`csrMode`/
   * `claimConfigVersions`/`judgeScore` already do.
   */
  skipReason: pipelineStepEventSkipReasonSchema.nullable().optional(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  model: z.string().nullable(),
  operation: z.string().nullable(),
  errorMessage: z.string().nullable(),
  ragChunks: pipelineRagChunksBlobSchema.nullable(),
  plainLanguageSummary: z.string().nullable(),
  llmInteraction: pipelineStepEventLlmInteractionViewSchema.nullable(),
  // Full RAG-stage trace (pirx-dnn42, Item 5) — only present on
  // `rag_retrieval` steps that ran after this epic shipped; nullable for
  // every pre-existing row and every non-rag_retrieval step. Deliberately
  // does NOT carry the raw pre-rank candidates (see `ragRawCandidateRowSchema`'s
  // doc comment) — those ride a separate on-demand endpoint, never this poll.
  ragTrace: ragRetrievalTraceSchema.nullable().optional(),
  /**
   * CSR-mode outcome for this event's claim (pirx-cijf6.4) — null when
   * `claimId` is null (non-claim step). Optional (not just nullable) so
   * pre-existing fixture/test object literals that predate this field keep
   * type-checking, same convention `ragTrace` above already uses.
   */
  csrMode: pipelineTransparencyClaimCsrModeSchema.nullable().optional(),
  /**
   * `configVersions` from the joined `claims` row (pirx-g0m2l.6 T6/T7) —
   * the `{ [configId]: version }` stamp of the config (prompt/prompt-set/
   * rubric) versions that produced this claim's classification output.
   * Null when `claimId` is null (non-claim step), when the LEFT JOIN
   * misses, or when the claim row predates T6/T7. Optional for the same
   * pre-existing-fixture reason `csrMode`/`ragTrace` above are optional.
   */
  claimConfigVersions: z.record(z.string(), z.string()).nullable().optional(),
  /**
   * Judge verdict for this row's segment (pirx-xmvo3.2) -- only ever
   * populated on a `stage: "judge_scoring"` row whose judge call
   * succeeded. Null on every other row, and on a judge row whose call
   * failed or timed out (that row carries `status: "failed"` and an
   * `errorMessage` instead -- never a partial blob). Optional for the same
   * pre-existing-fixture reason `csrMode`/`ragTrace` above are optional.
   */
  judgeScore: pipelineJudgeScoreBlobSchema.nullable().optional(),
});
export type PipelineStepEvent = z.infer<typeof pipelineStepEventSchema>;

/**
 * The audit/company profile fields shown on the "context used for this
 * audit" panel (pirx-qhoqc) — a fixed set, not every `audit_context`
 * column. `communicationPurpose` now has its own dedicated panel
 * (`PipelineTransparencyCsrSummary` below) instead of living in this flat
 * field list — its richer shape (declared vs. content-classifier verdict,
 * override ack) doesn't fit a single label/value row. `vulnerableAudience`
 * stays excluded: it has no capture path anywhere in the upload UI today
 * (no form field sets it), so it is genuinely always NULL, not just
 * under-adopted.
 */
export const auditContextFieldNameSchema = z.enum([
  "industrySector",
  "targetAudience",
  "mediumType",
  "geographicScope",
  "language",
  "declaredCertificate",
]);
export type AuditContextFieldName = z.infer<typeof auditContextFieldNameSchema>;

/**
 * i18n key suffix (under `internal:transparency.auditContext.fieldEffects.*`)
 * for each field's honest, static effect description. Keyed 1:1 against
 * `auditContextFieldNameSchema` and guarded by a completeness test below —
 * mirrors `STEP_DESCRIPTION_KEYS`'s discipline exactly.
 */
export const AUDIT_CONTEXT_FIELD_EFFECT_KEYS: Record<
  AuditContextFieldName,
  string
> = {
  industrySector: "industrySector",
  targetAudience: "targetAudience",
  mediumType: "mediumType",
  geographicScope: "geographicScope",
  language: "language",
  declaredCertificate: "declaredCertificate",
};

/**
 * Returns the i18n key suffix for a field's effect description, or null for
 * an unregistered field name. Takes a plain `string` (not
 * `AuditContextFieldName`) for the same reason `getStepDescriptionKey` does.
 */
export function getAuditContextFieldEffectKey(field: string): string | null {
  return (AUDIT_CONTEXT_FIELD_EFFECT_KEYS as Record<string, string>)[field] ??
    null;
}

/**
 * The per-audit values for the panel above — raw captured values only. The
 * "what it does" explanation lives in i18n copy (`fieldEffects.*`), kept
 * separate from the data, same separation `STEP_DESCRIPTION_KEYS` uses for
 * step names vs. their descriptions.
 *
 * `declaredCertificate` is sourced from the company profile (not
 * `audit_context`), specifically the as-of-that-audit snapshot in
 * `assessment_reports.company_data` — not the company's current live
 * settings. It's a union because `company_data.declared_certificate` has
 * both a legacy scalar-string shape and a newer string[] shape (multi-cert).
 */
// ---------------------------------------------------------------------------
// pirx-8wdz1: canonical declared-vs-detected disagreement shape.
//
// Used by:
//   - `pipelineTransparencyCsrSummarySchema.purposeDisagreement` (pre-screening
//     -- declared communication_purpose vs content-classifier verdict; the
//     boolean flag there is a back-compat alias for this shape's `disagreement`)
//   - `pipelineTransparencyAuditContextSummarySchema.sectorsDisagreement` (new
//     in pirx-26iew.6 -- declared NACE divisions vs LLM sector-classifier
//     verdict; surfaced on the transparency page alongside industrySector).
// Future override sites (e.g. declared-cert vs future cert-detection) MUST
// reuse this shape, not invent their own one-off field. The closed decision
// in pirx-8wdz1 explicitly rejects per-site shapes for exactly this reason.
// ---------------------------------------------------------------------------

/** Generic declared-vs-detected disagreement shape. Per-site schemas narrow declared/detected to the per-signal types. */
export const signalDisagreementSchema = z.object({
  /** Value the user (or upstream signal) declared -- raw or derived. */
  declared: z.unknown(),
  /** Value the model / classifier detected. */
  detected: z.unknown(),
  /** True when declared and detected disagree (per the override policy: detected wins, declared is surfaced, never silently overridden). */
  disagreement: z.boolean(),
  /** Optional human-readable rationale for the disagreement (e.g. the LLM classifier's rationale field). */
  reason: z.string().optional(),
});
export type SignalDisagreement = z.infer<typeof signalDisagreementSchema>;

/**
 * Sectors-specific disagreement (pirx-26iew.6).
 *
 * Tracks the three independent axes required by pirx-8wdz1's resolved decision:
 *
 *   1. Canonical/surfaced value -- detected (detectedDivisions).
 *      This is the value reported on the transparency page and used for any
 *      downstream decision that needs the answer (e.g. report commentary
 *      classifying the audit's industry).
 *   2. User-declared value -- declaredDivisions, derived from the company's
 *      industry_sector selection via divisionsFromDeclaredSections (backend
 *      services/legal-corpus/sector-classifier.ts). Always surfaced alongside
 *      detectedDivisions; never silently dropped.
 *   3. Retrieval filter set -- retrievalWidenedDivisions, the union used by
 *      QdrantFlatClient.buildFilter (commit f15f1ecda, pirx-26iew.5). Per
 *      pirx-8wdz1's open-sub-question resolution and the locked ADR
 *      (docs/adr/2026-08-04-legal-corpus-sector-filtering-nace-classifier.md),
 *      retrieval WIDENS rather than narrows on disagreement -- over-inclusion
 *      costs precision, not correctness. Surfaced so the user can audit
 *      exactly which divisions their query actually searched against.
 *
 * detectedConfidence is the LLM classifier's self-reported confidence
 * (high / medium / low, per the classifier schema in
 * backend/src/services/legal-corpus/sector-classifier.ts). Null when the
 * classifier didn't run or returned no result (e.g. business_activity is
 * empty, or the LLM call failed and the audit fell back to declared-only
 * filtering).
 */
/**
 * Why `detectedDivisions` is empty, distinguishing a genuinely skipped
 * classifier call from three real invocation failures that also collapse
 * to an empty detected set (pirx-vyj6d). Mirrors
 * `SectorClassifierFailureReason`
 * (`backend/src/services/legal-corpus/sector-classifier.ts`).
 */
export const sectorClassifierFailureReasonSchema = z.enum([
  /** business_activity was empty/missing -- classifySector was never called. */
  "not-invoked",
  /** The LLM responded but its output failed schema validation. */
  "validation-failed",
  /** The LLM responded, but every returned division code was invalid/hallucinated. */
  "no-valid-divisions",
  /** The classifier LLM call itself threw (network, quota, parse-repair failure, ...). */
  "call-failed",
]);
export type SectorClassifierFailureReasonWire = z.infer<
  typeof sectorClassifierFailureReasonSchema
>;

export const sectorsDisagreementSchema = z.object({
  /** NACE divisions implied by the declared industry_sector (via divisionsFromDeclaredSections). Empty array if undeclared. */
  declaredDivisions: z.array(z.string()),
  /** NACE divisions detected by the LLM sector classifier. Empty array if the classifier didn't run or returned null. */
  detectedDivisions: z.array(z.string()),
  /** True when the detected set diverges from the declared set (e.g. declared [G47] but classifier returned [G47, C20]). */
  disagreement: z.boolean(),
  /** Confidence of the LLM classification. Null when the classifier didn't run. */
  detectedConfidence: z.enum(["high", "medium", "low"]).nullable(),
  /** Actual retrieval filter set used (per the widen-on-disagreement policy) -- the union of declared and detected, in declared-then-detected order. */
  retrievalWidenedDivisions: z.array(z.string()),
  /** Human-readable rationale from the classifier (its rationale field). Optional. */
  reason: z.string().optional(),
  /** Why detectedDivisions is empty, or null when the classifier produced a real verdict. Optional for back-compat with rows persisted before pirx-vyj6d -- absent is treated the same as "not-invoked" by the frontend. */
  classifierFailureReason: sectorClassifierFailureReasonSchema.nullable()
    .optional(),
});
export type SectorsDisagreement = z.infer<typeof sectorsDisagreementSchema>;
export const pipelineTransparencyAuditContextSummarySchema = z.object({
  industrySector: z.string().nullable(),
  targetAudience: z.string().nullable(),
  mediumType: z.string().nullable(),
  geographicScope: z.string().nullable(),
  language: z.string().nullable(),
  declaredCertificate: z.union([z.string(), z.array(z.string())]).nullable(),
  /**
   * Sectors-disagreement record (pirx-26iew.6, schema from pirx-8wdz1).
   * Populated for audits that ran the sector classifier (pirx-26iew.4,
   * backend services/legal-corpus/sector-classifier.ts); null for audits
   * that predate the sector-classifier epic, or for audits where the
   * classifier wasn't applicable (e.g. business_activity was empty).
   * Optional to preserve back-compat with existing audit rows that predate
   * the sector classifier entirely.
   */
  sectorsDisagreement: sectorsDisagreementSchema.nullable().optional(),
});
export type PipelineTransparencyAuditContextSummary = z.infer<
  typeof pipelineTransparencyAuditContextSummarySchema
>;

/** Server-stamped D2 CSR override acknowledgement, as surfaced on the transparency page. Mirrors `CsrOverrideAcknowledgement` (`packages/shared/src/types.ts`). */
export const pipelineTransparencyCsrOverrideSchema = z.object({
  reason: z.string().optional(),
  csrOverrideAcknowledgedAt: z.string(),
  csrOverrideAcknowledgedBy: z.string(),
});

/**
 * CSR mode axis (D8) + D2 override state captured for this audit
 * (pirx-cijf6.4) — the single highest-impact piece of user configuration in
 * this pipeline: it can flip a claim's report framing between legal and
 * reputational and drop legal citations entirely, and until now nothing on
 * the transparency page showed it. Null under the same degrade rule as
 * `PipelineTransparencyAuditContextSummary` (audit predates `audit_context`,
 * or `auditId` isn't a real audit).
 */
export const pipelineTransparencyCsrSummarySchema = z.object({
  /** Raw declared value from `audit_context.communication_purpose`; null = undeclared (ambiguity-default: analyze as commercial). */
  communicationPurpose: z.enum(["commercial", "educational_csr"]).nullable(),
  /** `resolveAnalysisMode()` over the declared context — `"csr"` only when `communicationPurpose === "educational_csr"`. */
  declaredMode: analysisModeAxisSchema,
  /** Content classifier's verdict at intake time, independent of the declared purpose. Null when pre-screening never ran (pre-byh4n audits). */
  contentIsCommercialPractice: z.boolean().nullable(),
  contentConfidence: z.enum(["high", "medium", "low"]).nullable(),
  /** True when the declared purpose disagreed with the content classifier at intake — recorded, never used to silently override. */
  purposeDisagreement: z.boolean().nullable(),
  /** Present only when the D2 override was honored for this audit (`CSR_MODE_OVERRIDE_GLOBAL` + the team's `csr_mode_override_allowed` allowlist both on at submit time). */
  override: pipelineTransparencyCsrOverrideSchema.nullable(),
});
export type PipelineTransparencyCsrSummary = z.infer<
  typeof pipelineTransparencyCsrSummarySchema
>;

// ---------------------------------------------------------------------------
// pirx-cvwv2.3: envelope schemas.
//
// The inner PipelineStepEvent shape above has always been zod-validated
// here. Every shape *wrapping* it, though, was hand-retyped separately in
// the backend service (backend/src/services/internal/pipeline-transparency.service.ts,
// backend/src/services/internal/langfuse-cost.service.ts) and the frontend
// client (frontend/src/services/api/pipeline-transparency-client.ts), with
// nothing checking the two sides agree. These schemas are that missing
// source of truth; pirx-cvwv2.5 wires both sides to use them.
//
// Fields typed `Date` on the backend's own in-process service return types
// (`createdAt` below) are `z.string()` here deliberately — these schemas
// describe the wire shape (post-JSON-serialization), which is what the
// frontend actually receives, not the backend's pre-serialization
// in-memory type.
// ---------------------------------------------------------------------------

export const pipelineTransparencyInteractionSchema = z.object({
  id: z.string(),
  operation: z.string(),
  modelName: z.string(),
  requestPrompt: z.string(),
  responseRaw: z.string().nullable(),
  latencyMs: z.number().nullable(),
  createdAt: z.string(),
  /** Only set for generate_fixed_document rows — powers the before/after diff view. */
  originalDocumentText: z.string().optional(),
  /**
   * Cataloged prompt identity (pirx-1ryur T4/T5, spec
   * docs/specs/prompt-version-registry.md) that produced `requestPrompt`,
   * and the `PROMPT_VERSIONS` version it resolved to at write time. Both
   * null (never coerced to a placeholder) for rows written before T4
   * landed, or when the call site never threaded a cataloged `PromptId`.
   */
  promptId: z.string().nullable(),
  promptVersion: z.string().nullable(),
});
export type PipelineTransparencyInteractionWire = z.infer<
  typeof pipelineTransparencyInteractionSchema
>;

export const pipelineTransparencyFeedbackSchema = z.object({
  id: z.string(),
  userId: z.string(),
  rating: z.number(),
  comment: z.string().nullable(),
  createdAt: z.string(),
});
export type PipelineTransparencyFeedbackWire = z.infer<
  typeof pipelineTransparencyFeedbackSchema
>;

/** `GET /api/internal/pipeline-transparency/:auditId` response envelope. */
export const pipelineTransparencyResultSchema = z.object({
  auditId: z.string(),
  interactions: z.array(pipelineTransparencyInteractionSchema),
  feedback: z.array(pipelineTransparencyFeedbackSchema),
  auditContextSummary: pipelineTransparencyAuditContextSummarySchema
    .nullable(),
  csrMode: pipelineTransparencyCsrSummarySchema.nullable(),
});
export type PipelineTransparencyResultWire = z.infer<
  typeof pipelineTransparencyResultSchema
>;

export const auditFailureAttributionSchema = z.object({
  claimId: z.string().nullable(),
  stepEventId: z.string(),
  errorMessage: z.string().nullable(),
});
export type AuditFailureAttributionWire = z.infer<
  typeof auditFailureAttributionSchema
>;

/**
 * `GET /api/internal/pipeline-runs/:runId/events` response envelope.
 *
 * `runStatus` uses the shared `pipelineRunStatusSchema` enum — the frontend
 * client previously hardcoded this as an inline literal union instead of
 * importing the shared type, a real (if latent) drift risk fixed by
 * pirx-cvwv2.5 switching it to this schema's inferred type.
 */
export const pipelineRunEventsResultSchema = z.object({
  runId: z.string(),
  events: z.array(pipelineStepEventSchema),
  runStatus: pipelineRunStatusSchema.nullable(),
  /**
   * `configVersions` from the run's own `audits` row (pirx-g0m2l.6 T6) —
   * the `{ [configId]: version }` stamp of the config versions that
   * produced this audit's classification output. Null for rows written
   * before T6/T7 landed, or when no audit row matches `runId`. Optional
   * for the same pre-existing-fixture reason `auditFailureAttribution`
   * below is optional.
   */
  runConfigVersions: z.record(z.string(), z.string()).nullable().optional(),
  auditFailureAttribution: auditFailureAttributionSchema.optional(),
});
export type PipelineRunEventsResultWire = z.infer<
  typeof pipelineRunEventsResultSchema
>;

/** `GET /api/internal/pipeline-runs/:runId/feedback` response envelope. */
export const pipelineRunFeedbackResultSchema = z.object({
  runId: z.string(),
  feedback: z.array(pipelineTransparencyFeedbackSchema),
});
export type PipelineRunFeedbackResultWire = z.infer<
  typeof pipelineRunFeedbackResultSchema
>;

export const pipelineRunCostCallSchema = z.object({
  llmInteractionId: z.string(),
  model: z.string().nullable(),
  /**
   * Langfuse's cost for this call, or `null` when Langfuse returned no
   * price for it (pirx-pj740). `null` means "unpriced", NOT "free" — a
   * consumer summing cost must treat the total as a floor while any call
   * is null instead of folding it in as $0.00. Retrieval (vector-search)
   * rows are not model calls and are omitted from `calls` entirely.
   */
  cost: z.number().nullable(),
});
export type PipelineRunCostCallWire = z.infer<
  typeof pipelineRunCostCallSchema
>;

/** `GET /api/internal/pipeline-runs/:runId/cost` response envelope. */
export const pipelineRunCostResultSchema = z.object({
  runId: z.string(),
  /** False when Langfuse isn't configured or the lookup failed — render the "not available" placeholder, not an error. */
  available: z.boolean(),
  calls: z.array(pipelineRunCostCallSchema),
});
export type PipelineRunCostResultWire = z.infer<
  typeof pipelineRunCostResultSchema
>;

// ---------------------------------------------------------------------------
// pirx-e9z6b.1: shared step-event wrapper (recordClaimStepEvent) + sink seam.
//
// Moves the timing wrapper, its context/extract types, and the
// registered-default-sink seam out of
// backend/src/services/methodology/orchestrator/telemetry.ts and into this
// shared module, so every domain (methodology, gemini/stages, assessments,
// audits, evidence, rag, messaging) can import the verb from one place
// instead of reaching backwards into methodology's orchestrator module. The
// backend keeps the only DB-aware piece (writeStepEvent, wired in as the
// registered sink via installStepEventSink) — this module has no Drizzle,
// no Database, and no backend-only type.
// ---------------------------------------------------------------------------

export interface ClaimStepEventContext {
  /**
   * `processOneClaimInternal` has no audit/claim identity of its own — the
   * only run-scoped value threaded into it is `options.cacheSessionKey`
   * (== `clientRunId`, == `auditId` for real queued audits per
   * `audit-queue.ts`'s default-synthesized `methodologyRequest`). Undefined
   * for callers that never set it (e.g. some CLI/benchmark paths) — the
   * wrapper silently skips writing in that case rather than writing a
   * `run_id` that doesn't resolve to a real `audits` row.
   */
  runId: string | undefined;
  /**
   * Pre-generated correlation key (pirx-tn7uj), sourced from
   * `options.claimId` — set by `stages/batch.ts` once per claim task, before
   * `processOneClaimFn` is invoked, so every step event a given claim's
   * processing emits shares the same id. Opaque, NOT a foreign key to
   * `claims.id`: that table is populated by a separate, earlier raw-claim
   * extraction write path (`audit-queue.ts`, before any RAG/analysis runs),
   * with no live 1:1 correspondence to this function's per-claim output to
   * tie a real FK to. Undefined for callers that never set
   * `options.claimId` (e.g. some CLI/benchmark paths) — the wrapper still
   * writes the event, just without a claim grouping key. AC6's correctness
   * guarantee does not depend on this field either way: each concurrent
   * call's `llmInteractionId` is captured via its own closure, never shared
   * mutable state, so cross-claim attribution can't happen regardless of
   * whether `claim_id` is populated.
   */
  claimId?: string;
}

/**
 * Metadata a step's wrapped call can surface for its event row, pulled from
 * the step's own (unchanged) return value — the wrapper never alters what
 * `processOneClaimInternal` sees or does with that value, only what gets
 * reported here (mirrors the step-wrapper-composition spike's contract,
 * pirx-p7129).
 */
export interface ClaimStepEventExtract {
  /** Presence marks the event `failed` even though `fn` resolved normally (the 4 self-swallowing steps, pirx-p7129). */
  errorMessage?: string;
  model?: string;
  operation?: string;
  llmInteractionId?: string;
  /** Bounded blob written by `boundRagChunks` (backend grounding/rag-retrieval-handler.ts) — only present on `rag_retrieval` steps. `truncated` lives inside the blob (not a separate column) so the AC8 truncation indicator always travels with the data it describes. Typed via the shared `PipelineRagChunksBlob` (structurally identical to the backend's `BoundedRagChunks`) so this module stays free of backend-only types. */
  ragChunks?: PipelineRagChunksBlob;
  /** Full RAG-stage trace (pirx-dnn42.5) — only present on `rag_retrieval` steps. Rides the poll (small: params/queries/stage counts, no raw candidate payloads). A SIBLING of `ragRawCandidates`, never nested — see that field's own doc comment. */
  ragTrace?: RagRetrievalTrace;
  /** Raw pre-rank Qdrant candidates (pirx-dnn42.5) — NEVER selected by `getPipelineRunEvents`; on-demand route only. Must stay a sibling of `ragTrace` in the registered sink's column mapping, never merged into it — that would silently reintroduce the payload-leakage cost the on-demand endpoint exists to prevent. */
  ragRawCandidates?: RagRawCandidateRow[];
  plainLanguageSummary?: string;
  /** Reason code for a `status: "skipped"` event (pirx-claoz) — only meaningful alongside that status; ignored otherwise. */
  skipReason?: PipelineStepEventSkipReason;
  /** Telemetry (attempt count + triggered checks) for the report_generation step. */
  reportGenerationTelemetry?: {
    attemptCount: number;
    triggeredChecks: string[];
  };
}

/** Six-tuple sink signature a `recordClaimStepEvent` caller (or the registered default) receives on every resolve/throw. The backend's registered default sink is the only place that knows about `Database`/Drizzle — this module never does. */
export type StepEventSink = (
  ctx: ClaimStepEventContext,
  step: ClaimStepName,
  status: "completed" | "failed" | "skipped",
  startedAt: Date,
  durationMs: number,
  extra: ClaimStepEventExtract | undefined,
) => void;

/**
 * Builds a `ClaimStepEventContext` from whichever run-scoped identifiers a
 * caller has on hand. Verified `runId` sources across the ~30 production
 * call sites: `auditId`, `cacheSessionKey`, `job.auditId`,
 * `this.input.assessmentId`, `preparedInput.auditId`, `claim.id`, and bare
 * `undefined` for CLI/benchmark paths that have none of the above.
 * Deliberately broader than the grounding brief's
 * `{ cacheSessionKey?: string; claimId?: string }` shape.
 */
export function createStepEventContext(
  options: { runId?: string | undefined; claimId?: string },
): ClaimStepEventContext {
  return { runId: options.runId, claimId: options.claimId };
}

let defaultStepEventSink: StepEventSink | null = null;

/** Registers the sink `recordClaimStepEvent` falls through to when called without an explicit fifth `sink` argument. The backend's `initializeStepEventTelemetry` calls this once at startup with a closure over its DB connection. */
export function installStepEventSink(sink: StepEventSink): void {
  defaultStepEventSink = sink;
}

/** Clears the registered default sink — calls with no explicit `sink` become no-ops again, matching the backend's "no telemetry initialized" semantics. The backend's `shutdownStepEventTelemetry` calls this during graceful shutdown. */
export function clearStepEventSink(): void {
  defaultStepEventSink = null;
}
/**
 * Wrap one per-claim step. Times unconditionally; on a thrown error records
 * `status: "failed"` and re-throws; on a normal resolve, records `status:
 * "completed"` UNLESS `extract(result).errorMessage` is set — needed for the
 * 4 steps that already swallow their own errors internally and return a
 * fallback value instead of propagating (pirx-p7129's spike). Either way the
 * original `result` is returned unchanged: this wrapper only changes what
 * gets reported, never what the pipeline does with it.
 *
 * Hands the six-tuple to the explicit `sink` argument when given, otherwise
 * to the registered default (a no-op if neither is set — the same "no
 * telemetry initialized" semantics the backend's DB-gated write path used to
 * enforce directly).
 */
export async function recordClaimStepEvent<T>(
  ctx: ClaimStepEventContext,
  step: ClaimStepName,
  fn: () => Promise<T>,
  extract?: (result: T) => ClaimStepEventExtract | undefined,
  sink?: StepEventSink,
): Promise<T> {
  const startedAt = new Date();
  const perfStart = performance.now();
  const emit = sink ?? defaultStepEventSink;
  try {
    const result = await fn();
    const durationMs = Math.max(
      0,
      Math.round(performance.now() - perfStart),
    );
    const extra = extract?.(result);
    emit?.(
      ctx,
      step,
      extra?.errorMessage ? "failed" : "completed",
      startedAt,
      durationMs,
      extra,
    );
    return result;
  } catch (error) {
    const durationMs = Math.max(
      0,
      Math.round(performance.now() - perfStart),
    );
    emit?.(ctx, step, "failed", startedAt, durationMs, {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

/**
 * Emit an explicit `skipped` event for a step that a caller has decided
 * NOT to run for this claim (pirx-claoz) — e.g. a grounding-gate failure or
 * a RAG deadline/retry branch that returns before the step's own
 * `recordClaimStepEvent` call ever fires. Distinguishes "the pipeline chose
 * not to run this" from a dropped/lost event or a pre-instrumentation
 * audit, which are otherwise indistinguishable on the transparency page.
 *
 * Unlike `recordClaimStepEvent`, there is no `fn` to time: the step never
 * ran, so `durationMs` is always `0` and `startedAt`/`endedAt` collapse to
 * the moment the skip is recorded. Hands the six-tuple to the explicit
 * `sink` argument when given, otherwise to the registered default (a no-op
 * if neither is set, same as `recordClaimStepEvent`).
 */
export function recordSkippedStepEvent(
  ctx: ClaimStepEventContext,
  step: ClaimStepName,
  reason: PipelineStepEventSkipReason,
  sink?: StepEventSink,
): void {
  const emit = sink ?? defaultStepEventSink;
  emit?.(ctx, step, "skipped", new Date(), 0, { skipReason: reason });
}

/**
 * Record a "completed" (or "failed", if `extract` reports an error) event
 * for a step whose result is already synchronously available — telemetry
 * backfill over data computed earlier in the same function, or a
 * synchronous helper call, with no real asynchronous work to time. Unlike
 * `recordClaimStepEvent`, there is no `fn` to await: the caller passes the
 * already-computed `result` directly and gets it back unchanged, so
 * `durationMs` is always `0` (same rationale as `recordSkippedStepEvent`).
 * Status derivation mirrors `recordClaimStepEvent`'s own rule: `"failed"`
 * when `extract(result).errorMessage` is set, `"completed"` otherwise.
 */
export function recordSyntheticStepEvent<T>(
  ctx: ClaimStepEventContext,
  step: ClaimStepName,
  result: T,
  extract?: (result: T) => ClaimStepEventExtract | undefined,
  sink?: StepEventSink,
): T {
  const emit = sink ?? defaultStepEventSink;
  const extra = extract?.(result);
  emit?.(
    ctx,
    step,
    extra?.errorMessage ? "failed" : "completed",
    new Date(),
    0,
    extra,
  );
  return result;
}
